<template>
  <section class="admin-content" id="ticket-statistics">
    <Navbar />
    <main class="admin-main">
      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center justify-content-between text-white" style="padding: 32px 0;">
            <div class="d-flex align-items-center" style="gap: 16px;">
              <div class="header-icon-wrap">
                <i class="fe fe-bar-chart-2" style="font-size: 22px;"></i>
              </div>
              <div>
                <h4 class="mb-0 font-weight-bold">Ticket Statistics</h4>
                <small style="opacity: 0.8;">Monitor queue health, resolution throughput, and assignment load</small>
              </div>
            </div>
          </div>
        </div>
      </div>

      <section style="margin-top: -20px;">
        <div class="container pb-5">
          <div class="card shadow-sm filter-card mb-4">
            <div class="card-body px-4 py-4">
              <div class="form-row align-items-end">
                <div class="form-group col-md-4">
                  <label class="small font-weight-bold text-secondary">Provider</label>
                  <select class="form-control" v-model="filters.provider_id">
                    <option value="">All providers</option>
                    <option v-for="provider in providers" :key="provider.id" :value="provider.id">
                      {{ providerLabel(provider) }}
                    </option>
                  </select>
                </div>
                <div class="form-group col-md-3">
                  <label class="small font-weight-bold text-secondary">Start Date</label>
                  <input type="date" class="form-control" v-model="filters.start_date" />
                </div>
                <div class="form-group col-md-3">
                  <label class="small font-weight-bold text-secondary">End Date</label>
                  <input type="date" class="form-control" v-model="filters.end_date" />
                </div>
                <div class="form-group col-md-2 d-flex" style="gap: 8px;">
                  <button class="btn btn-primary btn-block" @click="getStats">Filter</button>
                  <button class="btn btn-outline-secondary btn-block" @click="resetFilters">Reset</button>
                </div>
              </div>
            </div>
          </div>

          <div class="row mb-4">
            <div class="col-md-3 mb-3 mb-md-0">
              <div class="card shadow-sm metric-card">
                <div class="card-body">
                  <p class="metric-label mb-1">Total Tickets</p>
                  <h4 class="metric-value mb-0">{{ stats.total }}</h4>
                </div>
              </div>
            </div>
            <div class="col-md-3 mb-3 mb-md-0">
              <div class="card shadow-sm metric-card metric-active">
                <div class="card-body">
                  <p class="metric-label mb-1">Active</p>
                  <h4 class="metric-value mb-0">{{ stats.active }}</h4>
                </div>
              </div>
            </div>
            <div class="col-md-3 mb-3 mb-md-0">
              <div class="card shadow-sm metric-card metric-resolved">
                <div class="card-body">
                  <p class="metric-label mb-1">Resolved</p>
                  <h4 class="metric-value mb-0">{{ stats.resolved }}</h4>
                </div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="card shadow-sm metric-card metric-completed">
                <div class="card-body">
                  <p class="metric-label mb-1">Completed</p>
                  <h4 class="metric-value mb-0">{{ stats.completed }}</h4>
                </div>
              </div>
            </div>
          </div>

          <div class="card shadow-sm insight-card">
            <div class="card-body px-4 py-4">
              <h6 class="font-weight-bold mb-3">Queue Snapshot</h6>
              <div class="insight-row">
                <span class="text-muted">Open workload</span>
                <strong>{{ stats.active }} ticket(s) still need action</strong>
              </div>
              <div class="insight-row">
                <span class="text-muted">Resolution rate</span>
                <strong>{{ resolutionRate }}%</strong>
              </div>
              <div class="insight-row">
                <span class="text-muted">Completion rate</span>
                <strong>{{ completionRate }}%</strong>
              </div>
            </div>
          </div>
        </div>

        <div class="vld-parent">
          <loading :active.sync="isLoading" loader="dots" :can-cancel="true" :is-full-page="fullPage"></loading>
        </div>
      </section>
    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";

export default {
  components: { Navbar, Loading },
  data() {
    return {
      agency: null,
      isLoading: false,
      fullPage: true,
      providers: [],
      filters: {
        provider_id: "",
        start_date: "",
        end_date: "",
      },
      stats: {
        total: 0,
        active: 0,
        resolved: 0,
        completed: 0,
      },
    };
  },
  computed: {
    resolutionRate() {
      if (!this.stats.total) {
        return 0;
      }

      return (((this.stats.resolved + this.stats.completed) / this.stats.total) * 100).toFixed(1);
    },
    completionRate() {
      if (!this.stats.total) {
        return 0;
      }

      return ((this.stats.completed / this.stats.total) * 100).toFixed(1);
    },
  },
  beforeMount() {
    this.agency = JSON.parse(localStorage.getItem("agency"));
  },
  methods: {
    normalizeCollection(payload) {
      if (Array.isArray(payload)) {
        return payload;
      }

      if (payload && Array.isArray(payload.data)) {
        return payload.data;
      }

      return [];
    },
    providerLabel(provider) {
      return provider.name || provider.provider_name || provider.full_name || provider.firstname || `Provider #${provider.id}`;
    },
    currentParams() {
      return {
        provider_id: this.filters.provider_id || undefined,
        start_date: this.filters.start_date || undefined,
        end_date: this.filters.end_date || undefined,
      };
    },
    fetchProviders() {
      this.axios
        .get(`/api/v1/auth/providerAgency/${this.agency.id}`)
        .then((response) => {
          this.providers = this.normalizeCollection(response.data);
        })
        .catch(() => {
          this.axios
            .get(`/api/v1/auth/allProviders`)
            .then((response) => {
              this.providers = this.normalizeCollection(response.data);
            })
            .catch((error) => {
              console.error(error);
            });
        });
    },
    getStats() {
      this.isLoading = true;
      this.axios
        .get(`/api/v1/auth/complaints/stats/${this.agency.id}`, { params: this.currentParams() })
        .then((response) => {
          this.stats = response.data;
          this.isLoading = false;
        })
        .catch((error) => {
          console.error(error);
          this.isLoading = false;
        });
    },
    resetFilters() {
      this.filters = {
        provider_id: "",
        start_date: "",
        end_date: "",
      };
      this.getStats();
    },
  },
  created() {
    this.fetchProviders();
    this.getStats();
  },
};
</script>

<style scoped>
.page-header {
  background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
  padding-bottom: 40px;
}

.header-icon-wrap {
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.filter-card,
.metric-card,
.insight-card {
  border-radius: 12px;
  border: 1px solid #e9ecef;
}

.metric-active {
  border-left: 4px solid #f59e0b;
}

.metric-resolved {
  border-left: 4px solid #16a34a;
}

.metric-completed {
  border-left: 4px solid #2563eb;
}

.metric-label {
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
  font-weight: 700;
}

.metric-value {
  font-weight: 700;
  color: #0f172a;
}

.insight-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #e2e8f0;
}

.insight-row:last-child {
  border-bottom: none;
}
</style>
